import { formatExitReason, calculateRemainingNativeQuantity } from '@einhaus/project314-shared';
import { formatNativeGrossValueEquation, formatQuantityWithUnit } from '@/utils/nativeQuantity';
import React from 'react';
import { getDateFromUnix, trimDecimals } from '@einhaus/meteoric-helpers';
import type {
    TradeTransactionWithFinancials,
    BacktestTradeTransaction,
    TradingStrategiesBacktestsTradeTransactionsRow,
    BrokerageOrder,
    AssetOption
} from '@einhaus/project314-types';
import OptionLink from '@/components/Entity/OptionLink.tsx';

type PendingBrokerageOrder = BrokerageOrder & {
    priceExecution?: {
        targetPrice: number;
        expiresAt: number;
        status: string;
        targetSourceIdentity: string;
    } | null;
};

/**
 * Renders transaction output in a styled format
 * Can be used for both UserPortfolioTrade transactions and BacktestTrade transactions
 * Also handles pending orders similar to generateCompactTransactionOutput
 *
 * @param transactions - Array of trade transactions to render
 * @param pendingOrders - Optional array of pending brokerage orders
 * @param compactMode - When true, uses a more compact layout suitable for small containers (250px or less)
 */
export const renderTransactionOutput = (config: {
    transactions: (TradeTransactionWithFinancials | BacktestTradeTransaction | TradingStrategiesBacktestsTradeTransactionsRow)[];
    pendingOrders?: PendingBrokerageOrder[];
    compactMode?: boolean;
    possibleOptions?: Map<string /* optionId_entityId_entityType */, AssetOption> | null;
}): React.ReactNode => {
    const { transactions, pendingOrders, compactMode } = config;
    const elements: React.ReactNode[] = [];

    // Render completed transactions
    transactions.forEach((transaction, index) => {
        const exitReasonOutput = transaction.exitReason
            ? ` (${formatExitReason(transaction.exitReason, 'exitDescription' in transaction ? transaction.exitDescription : null)})`
            : '';
        const isOption = transaction.derivativeType === 'Option';
        const optionGrossValueEquation = isOption
            ? formatNativeGrossValueEquation({
                  instrument: transaction,
                  quantity: transaction.quantity,
                  quotedPrice: transaction.price,
                  contractMultiplier: transaction.contractMultiplier
              })
            : null;

        const option =
            transaction.derivativeId && transaction.derivativeType === 'Option'
                ? config.possibleOptions?.get(`${transaction.derivativeId}_${transaction.entityId}_${transaction.entityType}`)
                : null;

        if (compactMode) {
            // Compact layout for small containers
            elements.push(
                <div className="transaction-item flex flex-col text-sm" key={`transaction-${index}`}>
                    <div className="flex items-center space-x-1">
                        <span className="transaction-type font-medium">{transaction.type}</span>
                        <span className="transaction-quantity">{formatQuantityWithUnit(transaction.quantity, transaction)}</span>
                        <span>@</span>
                        <span className="transaction-price">
                            ${trimDecimals(+transaction.price)}
                            {isOption ? ' premium' : ''}
                        </span>
                    </div>
                    {optionGrossValueEquation && <div className="text-xs text-zinc-400">{optionGrossValueEquation}</div>}
                    <div className="flex flex-wrap items-center">
                        <span className="transaction-date text-xs opacity-75">
                            {getDateFromUnix(transaction.timestamp, 'ymdhm', 'America/New_York')}
                        </span>
                        {exitReasonOutput && <span className="transaction-reason text-xs ml-1">{exitReasonOutput}</span>}
                        {'exitDescription' in transaction && transaction.exitDescription && (
                            <span className="text-xs w-full">{transaction.exitDescription}</span>
                        )}
                    </div>
                </div>
            );
        } else {
            // Standard layout for normal containers
            elements.push(
                <div className="transaction-item" key={`transaction-${index}`}>
                    <span className="transaction-type">{transaction.type}</span>
                    {option && (
                        <span className="transaction-option">
                            <OptionLink
                                description={option.description}
                                entity={{ entityId: transaction.entityId, entityType: transaction.entityType }}
                                optionId={option.id}
                            />
                        </span>
                    )}
                    <span className="transaction-quantity">{formatQuantityWithUnit(transaction.quantity, transaction)}</span> @
                    <span className="transaction-price">
                        ${trimDecimals(+transaction.price)}
                        {isOption ? ' premium' : ''}
                    </span>
                    {optionGrossValueEquation && (
                        <>
                            {' '}
                            <span className="transaction-gross">{optionGrossValueEquation}</span>
                        </>
                    )}
                    <span className="transaction-date">{getDateFromUnix(transaction.timestamp, 'ymdhm', 'America/New_York')}</span>
                    {exitReasonOutput && <span className="transaction-reason">{exitReasonOutput}</span>}
                    {'exitDescription' in transaction && transaction.exitDescription && (
                        <span className="text-xs">{transaction.exitDescription}</span>
                    )}
                </div>
            );
        }
    });

    // Render pending orders if provided
    if (pendingOrders && pendingOrders.length > 0) {
        pendingOrders.forEach((pendingOrder, index) => {
            const priceExecution = pendingOrder.priceExecution;
            const quantity = calculateRemainingNativeQuantity({
                instrument: pendingOrder,
                quantity: pendingOrder.quantity,
                quantityFilled: pendingOrder.quantityFilled
            });

            if (compactMode) {
                // Compact layout for small containers
                elements.push(
                    <div className="transaction-item flex flex-col text-sm" key={`pending-${index}`}>
                        <div className="flex items-center space-x-1">
                            <span className="transaction-type text-yellow-500 font-medium">{pendingOrder.type}</span>
                            <span className="transaction-quantity text-yellow-500">{formatQuantityWithUnit(quantity, pendingOrder)}</span>
                            <span className="text-yellow-500">@</span>
                            <span className="transaction-price text-yellow-500">
                                ${trimDecimals(pendingOrder.pricePerShare)}
                                {pendingOrder.derivativeType === 'Option' ? ' premium' : ''}
                            </span>
                        </div>
                        <div className="flex flex-wrap items-center">
                            <span className="transaction-date text-yellow-500 text-xs">{pendingOrder.insertDateTime}</span>
                            <span className="transaction-reason text-xs ml-1">(Pending {pendingOrder.brokerage})</span>
                        </div>
                        {priceExecution && (
                            <div className="text-xs text-amber-200">
                                Dynamic price entry · frozen ${trimDecimals(priceExecution.targetPrice)} · {priceExecution.status}
                            </div>
                        )}
                        <button
                            className="text-red-500 modify-order-link underline hover:text-red-400 text-xs mt-1"
                            data-orderid={pendingOrder.id}
                            data-ordertype={pendingOrder.type}
                            data-positionid={pendingOrder.positionId}
                            data-price={pendingOrder.pricePerShare}
                            data-quantity={quantity}
                            type="button"
                        >
                            {priceExecution ? 'View/Cancel' : 'Edit/Cancel'}
                        </button>
                    </div>
                );
            } else {
                // Standard layout for normal containers
                elements.push(
                    <div className="transaction-item" key={`pending-${index}`}>
                        <span className="transaction-type text-yellow-500">{pendingOrder.type}</span>
                        <span className="transaction-quantity text-yellow-500">{formatQuantityWithUnit(quantity, pendingOrder)}</span> @
                        <span className="transaction-price text-yellow-500">
                            ${trimDecimals(pendingOrder.pricePerShare)}
                            {pendingOrder.derivativeType === 'Option' ? ' premium' : ''}
                        </span>
                        <span className="transaction-date text-yellow-500">{pendingOrder.insertDateTime}</span>
                        <span className="transaction-reason">(Pending {pendingOrder.brokerage})</span>
                        {priceExecution && (
                            <span className="transaction-reason text-amber-200">
                                (Dynamic price entry · frozen ${trimDecimals(priceExecution.targetPrice)} · {priceExecution.status})
                            </span>
                        )}
                        <button
                            className="text-red-500 modify-order-link underline hover:text-red-400"
                            data-orderid={pendingOrder.id}
                            data-ordertype={pendingOrder.type}
                            data-positionid={pendingOrder.positionId}
                            data-price={pendingOrder.pricePerShare}
                            data-quantity={quantity}
                            type="button"
                        >
                            {priceExecution ? 'View/Cancel' : 'Edit/Cancel'}
                        </button>
                    </div>
                );
            }
        });
    }

    return <div className={`transaction-output break-words ${compactMode ? 'text-xs space-y-2' : ''}`}>{elements}</div>;
};
